import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { receivablesApi, receivablesQueryKeys } from "../../shared/api/receivables";
import { formatVndWithUnit } from "../../shared/format";
import { PageHeader } from "../../shared/ui/PageHeader";
import { ReportErrorState } from "../../shared/ui/ReportErrorState";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const BUCKET_ORDER = ["0-30", "31-60", "61-90", "90+"];
const BUCKET_LABEL: Record<string, string> = {
  "0-30": "0–30 ngày",
  "31-60": "31–60 ngày",
  "61-90": "61–90 ngày",
  "90+": "Trên 90 ngày",
};

/** Công nợ — accounts-receivable aging report. */
export function ReceivablesPage() {
  const q = useQuery({
    queryKey: receivablesQueryKeys.report(),
    queryFn: () => receivablesApi.report(),
  });

  const report = q.data;
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  // #19865: danh sách con nợ mang tên bệnh nhân — xuất PHÍA SERVER để có dòng kiểm toán.
  const exportCsv = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await receivablesApi.exportCsv();
    } catch (e) {
      setExportError((e as Error).message);
    } finally {
      setExporting(false);
    }
  };
  const byBucket = new Map(report?.aging.map((a) => [a.bucket, a]) ?? []);

  return (
    <div>
      <PageHeader
        title="Công nợ"
        description="Các hóa đơn còn nợ (chưa thu đủ), phân theo tuổi nợ tính từ ngày xuất hóa đơn."
        actions={
          <Button
            variant="outline"
            onClick={exportCsv}
            disabled={exporting || !report || report.debtors.length === 0}
          >
            {exporting ? "Đang xuất…" : "Xuất CSV"}
          </Button>
        }
      />
      {exportError && (
        <p className="mb-3 text-sm text-destructive" role="alert">
          {exportError}
        </p>
      )}

      {q.isLoading && <p className="text-sm text-muted-foreground">Đang tải…</p>}
      {/* #19850 — trước đây lỗi chỉ để lại một trang trống, trông như "không ai nợ". */}
      {q.isError && (
        <ReportErrorState error={q.error} what="báo cáo công nợ" onRetry={() => q.refetch()} />
      )}

      {report && (
        <div className="grid gap-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <Card>
              <CardContent className="p-4">
                <div className="text-sm text-muted-foreground">Tổng công nợ</div>
                <div className="text-xl font-semibold text-foreground">
                  {formatVndWithUnit(report.totalOutstanding)}
                </div>
              </CardContent>
            </Card>
            {BUCKET_ORDER.map((b) => (
              <Card key={b}>
                <CardContent className="p-4">
                  <div className="text-sm text-muted-foreground">{BUCKET_LABEL[b]}</div>
                  <div className="text-lg font-semibold text-foreground">
                    {formatVndWithUnit(byBucket.get(b)?.amount ?? 0)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {byBucket.get(b)?.count ?? 0} hóa đơn
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card>
            <CardContent className="p-4">
              <h3 className="mb-3 text-sm font-semibold text-muted-foreground">
                Bệnh nhân còn nợ
              </h3>
              {report.debtors.length === 0 ? (
                <p className="text-sm text-muted-foreground">Không có công nợ.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Bệnh nhân</TableHead>
                      <TableHead className="text-right">Số hóa đơn</TableHead>
                      <TableHead className="text-right">Nợ nhiều nhất (ngày)</TableHead>
                      <TableHead className="text-right">Còn nợ</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.debtors.map((d) => (
                      <TableRow key={d.patientId}>
                        <TableCell>
                          <Link className="text-primary hover:underline" to={`/patients/${d.patientId}`}>
                            {d.patientName}
                          </Link>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{d.invoiceCount}</TableCell>
                        <TableCell className="text-right tabular-nums">{d.maxAgeDays}</TableCell>
                        <TableCell className="text-right font-medium tabular-nums">
                          {formatVndWithUnit(d.balance)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
